import React from 'react';
import { Link } from 'react-router-dom';
import axios from "axios";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import config from '../../config';

const CorporateActions = () => {
    const companyId = useSelector((state) => state.user.companyId);
    const [isLoading, setIsLoading] = useState(false);
    const [cashLedgerList, setCashLedgerList] = useState([])
    const [err, setErr] = useState("")
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    
    // Filter states
    const [inputs, setInputs] = useState({
        scripName: "",
        fromExDate: "",
        toExDate: ""
    });

    const email = useSelector((state) => state.user.email);
    const navigate = useNavigate();
    useEffect(() => {
        if (email === null || email === "") {
            navigate("/")
        }
    })

    useEffect(() => {
        // Initial data fetch
        if (companyId) {
            fetchCorporateActions(1, pageSize);
        }
    }, [companyId])

    // Fetch corporate actions with pagination
    const fetchCorporateActions = async (page = 1, size = pageSize) => {
        try {
            setIsLoading(true);
            setErr("");
            const requestBody = {
                scripName: inputs.scripName || null,
                fromExDate: inputs.fromExDate || null,
                toExDate: inputs.toExDate || null,
                companyId: companyId
            };

            const response = await axios.post(
                `${config.BL_API_URL}/corporateAction/v1/searchCorporateActions?page=${page - 1}&size=${size}`,
                requestBody,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.content) {
                setCashLedgerList(response.data.content);
                setTotalRecords(response.data.totalElements);
                setTotalPages(response.data.totalPages);
                setCurrentPage(page);
            } else {
                setCashLedgerList([]);
                setTotalRecords(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error("Error fetching corporate actions:", error);
            setErr("Error fetching corporate actions. Please try again.");
            setCashLedgerList([]);
            setTotalRecords(0);
            setTotalPages(0);
        } finally {
            setIsLoading(false);
        }
    };

    const deleteCashLedger = async (id) => {
        if (!window.confirm("Are you sure you want to delete this record?")) {
            return;
        }

        try {
            setIsLoading(true);
            let ledgerId = Number(id);
            const res = await axios.get(config.BL_API_URL + "/CashLedger/v1/deleteById", { params: { ledgerId } });

            if (res.status === 200) {
                // Refresh the current page
                await fetchCorporateActions(currentPage, pageSize);
                setErr("");
            } else {
                setErr("Failed to delete record. Please try again.");
            }
        } catch (err) {
            if (err.response) {
                setErr(err.response.data);
            } else {
                setErr("Server error while deleting record. Please try again.");
            }
        } finally {
            setIsLoading(false);
        }
    }

    function formatNumber(value) {
        return new Intl.NumberFormat('en-IN').format(value);
    }

    function formatDate(value) {
        if (value == null || value == undefined || value == "") {
            return;
        }
        return new Intl.DateTimeFormat('en-IN').format(value);
    }

    // Pagination and search helper functions
    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            fetchCorporateActions(page, pageSize);
        }
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        fetchCorporateActions(1, newSize);
    };

    const handleSearch = () => {
        setCurrentPage(1); // Reset to first page when searching
        fetchCorporateActions(1, pageSize);
    };

    const handleClearSearch = () => {
        setInputs(prevInputs => {
            const clearedInputs = {
                scripName: "",
                fromExDate: "",
                toExDate: ""
            };
            // Call API after state update
            setTimeout(() => {
                setCurrentPage(1);
                fetchCorporateActions(1, pageSize);
            }, 0);
            return clearedInputs;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputs(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const formatDateForDisplay = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return date.toLocaleDateString('en-GB', options).replace(/ /g, '-');
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        // Previous button
        buttons.push(
            <button
                key="prev"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className="px-3 py-1 mx-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
                Previous
            </button>
        );

        // Page number buttons
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    disabled={isLoading}
                    className={`px-3 py-1 mx-1 text-sm border rounded ${
                        currentPage === i 
                            ? 'bg-blue-500 text-white border-blue-500' 
                            : 'hover:bg-gray-100'
                    }`}
                >
                    {i}
                </button>
            );
        }

        // Next button
        buttons.push(
            <button
                key="next"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className="px-3 py-1 mx-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
                Next
            </button>
        );

        return buttons;
    };

    return (
        <div className="mt-8 md:px-14 p-4 max-w-screen-2xl mx-auto">
            <div className="flex flex-col">
                <div className="-m-1.5 overflow-x-auto">

                    <div className="border rounded-lg divide-y divide-gray-200 ">
                        <div className='col-span-2'>
                            <div className=" col-span-1">
                                {/* <div className="py-3 px-4 flex justify-center items-center">
                                    <span className="text-gray-500 text-2xl font-bold">Corporate Actions</span>
                                </div> */}

                                {/* Search Bar */}
                                <div className='px-2 py-5'>
                                    <div className="flex gap-2 items-end overflow-x-auto">
                                        <div className="min-w-36 text-xs">
                                            <fieldset className='border-2 text-xs border-gray-200 p-1'>
                                                <legend className='mx-1 px-1'>Scrip Name</legend>
                                                <input
                                                    type="text"
                                                    name="scripName"
                                                    value={inputs.scripName}
                                                    onChange={handleChange}
                                                    placeholder="Enter scrip name"
                                                    className="w-full py-1 px-2 focus:outline-none text-xs"
                                                />
                                            </fieldset>
                                        </div>
                                        <div className="min-w-32 text-xs">
                                            <fieldset className='border-2 text-xs border-gray-200 p-1'>
                                                <legend className='mx-1 px-1'>From Ex Date</legend>
                                                <input
                                                    type="date"
                                                    name="fromExDate"
                                                    value={inputs.fromExDate}
                                                    onChange={handleChange}
                                                    className="w-full py-1 px-2 focus:outline-none text-xs"
                                                />
                                            </fieldset>
                                        </div>
                                        <div className="min-w-32 text-xs">
                                            <fieldset className='border-2 text-xs border-gray-200 p-1'>
                                                <legend className='mx-1 px-1'>To Ex Date</legend>
                                                <input
                                                    type="date"
                                                    name="toExDate"
                                                    value={inputs.toExDate}
                                                    onChange={handleChange}
                                                    className="w-full py-1 px-2 focus:outline-none text-xs"
                                                />
                                            </fieldset>
                                        </div>
                                        <div className="min-w-20 text-xs">
                                            <button 
                                                type="button" 
                                                disabled={isLoading} 
                                                onClick={handleSearch} 
                                                className="w-full py-2 px-2 inline-flex justify-center items-center gap-x-1 text-xs font-semibold rounded-lg border border-transparent bg-btnSecondary text-white hover:bg-btnPrimary disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap">
                                                {isLoading ? "Searching..." : "Search"}
                                            </button>
                                        </div>
                                        <div className="min-w-16 text-xs">
                                            <button 
                                                type="button"  
                                                onClick={handleClearSearch} 
                                                className="w-full py-2 px-2 text-xs font-semibold rounded-lg border border-transparent bg-btnSecondary text-white hover:bg-btnPrimary whitespace-nowrap">
                                                Clear
                                            </button>
                                        </div>
                                        <div className="flex-grow"></div>
                                        <div className="min-w-36 text-xs">
                                            <Link 
                                                to="/addCorporateAction" 
                                                className="w-full py-2 px-2 inline-flex justify-center items-center text-xs font-semibold rounded-lg border border-transparent bg-btnSecondary text-white hover:bg-btnPrimary whitespace-nowrap">
                                                Add Corporate Action
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Table Section with Pagination Controls */}
                        <div className="flex flex-col">
                            {/* Pagination Controls - Top Right */}
                            {totalRecords > 0 && (
                                <div className="mb-2 flex justify-end items-center">
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-medium text-gray-700">Show:</label>
                                            <select
                                                value={pageSize}
                                                onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                                                disabled={isLoading}
                                                className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value={5}>5</option>
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            <span className="text-sm text-gray-700">records per page</span>
                                        </div>
                                        <div className="text-sm text-gray-700">
                                            Total Records: {totalRecords}
                                        </div>
                                        <div className="text-sm text-gray-700">
                                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
                                        </div>
                                        <div className="flex items-center">
                                            {renderPaginationButtons()}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Error Display */}
                            {err && (
                                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                                    {err}
                                </div>
                            )}
                            
                            <div className="overflow-auto border rounded-lg" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-200">
                                        <tr style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                            <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-700 uppercase bg-gray-200">Scrip Name</th>
                                            <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-700 uppercase bg-gray-200">Record Date</th>
                                            <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-700 uppercase bg-gray-200">Ex Date</th>
                                            <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-700 uppercase bg-gray-200">Corporate Action Type</th>
                                            <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-700 uppercase bg-gray-200">Quantity / Ratio</th>
                                            <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-700 uppercase bg-gray-200">Comment</th>
                                            <th scope="col" className="px-6 py-3 text-start text-xs font-medium text-gray-700 uppercase bg-gray-200">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {isLoading ? (
                                            <tr>
                                                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                                    <div className="flex items-center justify-center">
                                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
                                                        Loading corporate actions...
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : cashLedgerList && cashLedgerList.length > 0 ? (
                                            cashLedgerList.map((cashLedger, index) => (
                                                <tr key={index} className={`${index % 2 === 0 ? '' : 'bg-gray-100'} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-200`}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{cashLedger.scripName}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{formatDateForDisplay(cashLedger.recordDate)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{formatDateForDisplay(cashLedger.exDate)}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{cashLedger.corporateActionType}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{cashLedger.quantity}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{cashLedger.comment}</td>                                                    
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                                                        <button
                                                            onClick={() => deleteCashLedger(cashLedger.ledgerId)}
                                                            className="text-red-500 hover:text-red-700"
                                                            disabled={isLoading}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className="text-center text-red-500 py-4">No records to display!</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CorporateActions;